import { Injectable } from '@angular/core';
import type { TranslateService } from '@ngx-translate/core';
import { Dashboards, registerDashboardsPlugins } from './dashboards-core';
import { DashboardWidgetHost } from './widgets/dashboard-widget-host';
import { DASHBOARD_WIDGET_TYPE } from './widgets/dashboard-widget.contract';
import { registerWidget } from './widgets/dashboard-widget-registry';
import type { DashboardWidgetPool } from './widgets/dashboard-widget-registry';
import { installDataBindingOptions } from './widgets/dashboard-data-binding';
import { installChartTypeOptions } from './widgets/dashboard-chart-types';
import {
    DashboardMapComponent,
    DASHBOARD_MAP_TYPE,
    registerMapOutlines,
} from './widgets/dashboard-map';
import { registerWidgetSidebarEntries } from './widgets/dashboard-widget-sidebar';
import { ProviderConnector } from './data/provider-connector';
import type { DashboardDataProvider } from './data/dashboard-provider.types';

@Injectable({ providedIn: 'root' })
export class DashboardRegistryService {
    private readonly providers = new Map<
        string,
        { provider: DashboardDataProvider; refs: number }
    >();
    private readonly owned = new WeakMap<object, ReadonlySet<string>>();
    private coreRegistered = false;

    constructor() {
        ProviderConnector.resolveProvider = (id, queryKey) =>
            this.providers.get(id)?.provider ?? this.providers.get(queryKey)?.provider;
    }

    /** Runs the one-time app-global registration: plugins, map outlines, and our own cell types. */
    ensureRegistered(): void {
        if (this.coreRegistered) {
            return;
        }
        registerDashboardsPlugins();
        registerMapOutlines();
        Dashboards.ComponentRegistry.registerComponent(DASHBOARD_WIDGET_TYPE, DashboardWidgetHost);
        Dashboards.ComponentRegistry.registerComponent(DASHBOARD_MAP_TYPE, DashboardMapComponent);
        this.coreRegistered = true;
    }

    ensureAuthoringRegistered(translate: TranslateService): void {
        this.ensureRegistered();
        installDataBindingOptions(translate);
        installChartTypeOptions();
        registerWidgetSidebarEntries();
    }

    /** Replaces one board's provider set; `owner` is the registering component instance. */
    registerProviders(owner: object, providers: readonly DashboardDataProvider[]): void {
        this.releaseProviders(owner);
        const ids = new Set<string>();
        for (const provider of providers) {
            const entry = this.providers.get(provider.id);
            if (!entry) {
                this.providers.set(provider.id, { provider, refs: 1 });
            } else {
                if (!ids.has(provider.id)) {
                    entry.refs += 1;
                }
                entry.provider = provider;
            }
            ids.add(provider.id);
        }
        this.owned.set(owner, ids);
    }

    /** Drops a board's registrations (call on its destroy); shared ids survive via ref-count. */
    releaseProviders(owner: object): void {
        const ids = this.owned.get(owner);
        if (!ids) {
            return;
        }
        this.owned.delete(owner);
        for (const id of ids) {
            const entry = this.providers.get(id);
            if (entry && --entry.refs <= 0) {
                this.providers.delete(id);
            }
        }
    }

    /** Registers dashboard widgets by key (idempotent per component). */
    registerDashboardWidgets(widgets: DashboardWidgetPool): void {
        for (const [key, registration] of Object.entries(widgets)) {
            registerWidget(key, registration);

            if (registration.replaces) {
                Reflect.set(
                    Dashboards.ComponentRegistry.types,
                    registration.replaces,
                    DashboardWidgetHost
                );
            }
        }
    }
}
